import React, { useState, useEffect, useCallback } from 'react';

import PropTypes from 'prop-types';
import { FaRegHeart, FaHeart } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { CSSTransition } from 'react-transition-group';
import styled, { css, keyframes } from 'styled-components';

import { trackWishlist } from '~/shared/actions';
import {
  useAccountActions,
  useWishlistIds,
} from '~/techstyle-shared/react-accounts';
import {
  useProductContext,
  useTabIndex,
} from '~/techstyle-shared/react-components';
import {
  defineMessages,
  useIntl,
  FormattedMessage,
} from '~/techstyle-shared/react-intl';
import { useSession } from '~/techstyle-shared/redux-core';

const growPulse = keyframes`
  from {
      opacity: 1;
      transform: scale3d(1, 1);
  }

  to {
    opacity: 0;
    transform: scale3d(2, 2, 1);
  }
`;

const ToggleButton = styled.button`
  border: none;
  display: flex;
  font-family: inherit;
  position: relative;
  transform-origin: center center;
  background-color: unset;
  padding: 0;

  &.enter-active {
    animation-name: ${growPulse};
    animation-duration: ${({ animationDuration }) => animationDuration}ms;
  }

  ${({ toggleButtonStyle }) => toggleButtonStyle};
`;

const baseHeartIconStyle = css`
  width: 14px;
  height: auto;
  color: ${({ theme }) => theme.colors.iconColorWishlist};
  ${({ heartIconStyle }) => heartIconStyle};
`;

const EmptyHeartIcon = styled(FaRegHeart)`
  ${baseHeartIconStyle}
  ${({ $heartIconStyle }) => $heartIconStyle && $heartIconStyle}
`;

const HeartIcon = styled(FaHeart)`
  ${baseHeartIconStyle}
  ${({ $heartIconStyle }) => $heartIconStyle && $heartIconStyle}
`;

const WishlisCTAWrapper = styled.div`
  display: flex;
  align-items: center;
  margin: 20px auto;
  cursor: pointer;

  ${({ theme }) => theme.pdpStyles.wishlist.ctaWrapper}
`;

const WishListLabel = styled.div`
  margin-left: 10px;
  color: ${({ theme }) => theme.colors.focusText};
  font-weight: 700;
  font-size: 14px;
`;

const defaultToggleButtonStyle = css`
  cursor: pointer;
`;

const intlAriaLabel = defineMessages({
  removeFromWishlist: {
    id: 'site_product_page.added_to_wishlist',
    defaultMessage: 'added to wishlist',
  },
  addToWishlist: {
    id: 'site_product_page.add_to_wishlist',
    defaultMessage: 'add to wishlist',
  },
});

function WishlistCTA({
  animationDuration = 700,
  heartIconStyle,
  product: productFromProps,
  onClick,
  tabIndex,
  toggleButtonStyle: toggleButtonStyleFromProps,
  autoTagPrefix = 'product',
  label,
  ...rest
}) {
  const { isLoggedIn } = useSession();
  const [isAnimating, setAnimating] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [frozenIsInWishlist, setFrozenIsInWishlist] = useState();
  const intl = useIntl();
  const actions = useAccountActions();
  const { hasProductId, networkStatus } = useWishlistIds();
  const productFromContext = useProductContext();
  const product = productFromProps || productFromContext;
  const { masterProductId } = product;
  const toggleButtonStyle =
    toggleButtonStyleFromProps || defaultToggleButtonStyle;
  tabIndex = useTabIndex(tabIndex);

  const dispatch = useDispatch();

  const realIsInWishlist = hasProductId(masterProductId);

  useEffect(() => {
    if (isAnimating || isLoading || !networkStatus.isUpToDate) {
      // we want to continue using the value of frozenIsInWishlist
    } else {
      // We want to set frozenIsInWishlist to null so we use the actual realIsInWishlist value
      setFrozenIsInWishlist(null);
    }
  }, [isAnimating, isLoading, networkStatus.isUpToDate]);

  const startAnimation = useCallback(() => {
    setFrozenIsInWishlist(realIsInWishlist);
    setAnimating(true);
    // isLoading keeps track that we're loading during the time between the user's click and addWishlistItemSuccess (changes wishlistId's networkStatus)
    setLoading(true);
  }, [realIsInWishlist]);

  const addToWishlist = useCallback(async () => {
    startAnimation();
    try {
      await actions.addWishlistItem(masterProductId);
    } catch {
      // any errors get caught here
    } finally {
      setLoading(false);
    }
  }, [actions, masterProductId, startAnimation]);

  const removeFromWishlist = useCallback(async () => {
    startAnimation();
    try {
      await actions.deleteWishlistItem(masterProductId);
    } catch {
      // any errors get caught here
    } finally {
      setLoading(false);
    }
  }, [actions, masterProductId, startAnimation]);

  // Finished entering animation
  // Optimistic UI pattern, pretend item is added/removed immediately
  const handleOnEntered = useCallback(() => {
    setAnimating(false);
    setFrozenIsInWishlist((prevState) => !prevState);
  }, []);

  const handleClick = useCallback(() => {
    if (!onClick) {
      if (realIsInWishlist) {
        removeFromWishlist();
      } else {
        addToWishlist();
        dispatch(trackWishlist({ product_id_hit: masterProductId.toString() }));
      }
    } else {
      onClick({
        removeFromWishlist,
        addToWishlist,
        realIsInWishlist,
      });
    }
  }, [
    onClick,
    removeFromWishlist,
    addToWishlist,
    realIsInWishlist,
    masterProductId,
    dispatch,
  ]);

  if (!masterProductId) {
    throw new Error(
      `WishlistToggleButton must be used with a valid Product (specified via the 'product' prop or via ProductContext)`
    );
  }

  const isInWishlist =
    frozenIsInWishlist != null ? frozenIsInWishlist : realIsInWishlist;

  return (
    <WishlisCTAWrapper
      onClick={handleClick}
      {...(isLoggedIn ? { 'data-cnstrc-btn': 'add-to-favorites' } : {})}
    >
      <CSSTransition
        in={isAnimating}
        exit={false}
        timeout={animationDuration}
        onEntered={handleOnEntered}
      >
        <ToggleButton
          animationDuration={animationDuration}
          aria-label={
            isInWishlist
              ? intl.formatMessage(intlAriaLabel.removeFromWishlist)
              : intl.formatMessage(intlAriaLabel.addToWishlist)
          }
          aria-pressed={isInWishlist}
          disabled={isAnimating}
          tabIndex={tabIndex}
          toggleButtonStyle={toggleButtonStyle}
          data-autotag={
            isInWishlist
              ? `${autoTagPrefix}-favorite-icon-filled`
              : `${autoTagPrefix}-favorite-icon-empty`
          }
          {...rest}
        >
          {isInWishlist ? (
            <HeartIcon $heartIconStyle={heartIconStyle} />
          ) : (
            <EmptyHeartIcon $heartIconStyle={heartIconStyle} />
          )}
        </ToggleButton>
      </CSSTransition>
      <WishListLabel>
        <FormattedMessage
          id="site_product_page.cta_add_to_favorites"
          defaultMessage="Add To Favorites"
        />
      </WishListLabel>
    </WishlisCTAWrapper>
  );
}

WishlistCTA.propTypes = {
  /**
   * animation when clicking button, in ms
   */
  animationDuration: PropTypes.number,
  /**
   * to pass in for auto tags
   */
  autoTagPrefix: PropTypes.string,
  heartIconStyle: PropTypes.any,
  label: PropTypes.string,
  onClick: PropTypes.func,
  /**
   * product data that's being added to wish list
   */
  product: PropTypes.object,
  /**
   * for tabbable/aria purposes
   */
  tabIndex: PropTypes.number,
  toggleButtonStyle: PropTypes.any,
};

export default WishlistCTA;
